/**
 * ScanningStage — cinematic full-bleed running state shown while an audit
 * is in progress. Uses motion-canvas connecting-dots in the background, a
 * cycling stage tracker, ticker counters, and category badges that light
 * up in sequence to communicate that real work is happening — even though
 * we don't have per-step progress events from the backend.
 *
 * The component is purely presentational. Polling for status continues in
 * the parent (AuditDetail) — once the audit completes the parent unmounts
 * this stage and renders the full report.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CheckCircle, CircleNotch, MagnifyingGlass, Sparkle, ChatCircleText, Lightning, ShieldCheck, Code } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import ConnectingDots from "@/components/ConnectingDots";

const STAGES = [
  { id: "fetch",   icon: MagnifyingGlass, label: "Fetching markup",        detail: "GET / HTTP/1.1" },
  { id: "parse",   icon: Code,            label: "Parsing DOM tree",       detail: "Tokenising 1,247 nodes" },
  { id: "seo",     icon: MagnifyingGlass, label: "Running SEO checks",     detail: "title · meta · canonical · sitemap" },
  { id: "geo",     icon: Sparkle,         label: "Scoring GEO signals",    detail: "llms.txt · JSON-LD · author E-E-A-T" },
  { id: "aeo",     icon: ChatCircleText,  label: "Scoring AEO signals",    detail: "FAQ · HowTo · Speakable · breadcrumbs" },
  { id: "perf",    icon: Lightning,       label: "Measuring performance",  detail: "TTFB · payload · compression" },
  { id: "sec",     icon: ShieldCheck,     label: "Inspecting SSL & headers", detail: "HSTS · CSP · X-Frame · cert" },
  { id: "a11y",    icon: CheckCircle,     label: "Auditing accessibility", detail: "alt · labels · landmarks · lang" },
  { id: "tech",    icon: Code,            label: "Fingerprinting tech stack", detail: "Matching 100+ patterns" },
  { id: "ai",      icon: Sparkle,         label: "Writing recommendations", detail: "Claude Sonnet · prioritised plan" },
];

// Color palette matching report sections — used to colour log lines & badges
const HUES = ["#1D4ED8", "#0891B2", "#9333EA", "#0F766E", "#B91C1C", "#6D28D9", "#334155"];

export default function ScanningStage({ audit }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [tick, setTick] = useState(0);
  const [logs, setLogs] = useState([]);
  const elapsed = useRef(0);
  const startedAt = useRef(Date.now());

  // Advance the active stage every ~2.4s so the user sees motion. We loop
  // back to the last stage and hold there if the audit takes longer than
  // STAGES * 2.4s (typical backend audit completes in 6–25s).
  useEffect(() => {
    if (audit?.status === "failed") return;
    const t = setInterval(() => {
      setActiveIdx((i) => Math.min(i + 1, STAGES.length - 1));
    }, 2400);
    return () => clearInterval(t);
  }, [audit?.status]);

  // 100ms ticker for the elapsed counter + animated byte/req counters
  useEffect(() => {
    if (audit?.status === "failed") return;
    const t = setInterval(() => {
      elapsed.current = Date.now() - startedAt.current;
      setTick((x) => x + 1);
    }, 100);
    return () => clearInterval(t);
  }, [audit?.status]);

  // Streaming log lines — pushes a synthetic but plausible technical line
  // every ~450ms so the right-hand log column always has fresh movement
  useEffect(() => {
    if (audit?.status === "failed") return;
    const lines = [
      "→ GET / 200 OK · 47 KB · gzip",
      "  <title> · 58 chars OK",
      "  <meta description> · 142 chars OK",
      "  H1 count: 1 OK",
      "  canonical: self-referential",
      "  og:image · twitter:card present",
      "→ HEAD /robots.txt 200",
      "→ HEAD /sitemap.xml 200",
      "→ HEAD /llms.txt 404 ⚠",
      "  JSON-LD scripts: 3 (Organization, WebSite, BreadcrumbList)",
      "  schema.org/FAQPage: not found ⚠",
      "  Speakable markers: 0 ⚠",
      "  data-test for question H2: 4",
      "  TTFB: 184 ms",
      "  HTML payload: 47.2 KB",
      "  external scripts: 12",
      "  Content-Security-Policy: set",
      "  Strict-Transport-Security: max-age=63072000",
      "  alt coverage: 28/35 images",
      "  → Cloudflare, Next.js, GA4, Stripe.js detected",
      "  Tech matches: 17 across 9 categories",
      "→ POST /llm/recommendations · drafting…",
    ];
    let i = 0;
    const t = setInterval(() => {
      setLogs((cur) => {
        const next = [...cur, { txt: lines[i % lines.length], hue: HUES[i % HUES.length], id: cur.length }];
        return next.slice(-12);
      });
      i++;
    }, 450);
    return () => clearInterval(t);
  }, [audit?.status]);

  const elapsedDisp = useMemo(() => {
    const s = (elapsed.current / 1000).toFixed(1);
    return s.padStart(4, "0");
  }, [tick]);

  // Plausible-looking running byte counter (linear ramp until 240 KB)
  const bytesDisp = useMemo(() => {
    const kb = Math.min(240, Math.round((elapsed.current / 100) * 1.7));
    return `${kb.toString().padStart(3, " ")} KB`;
  }, [tick]);

  // Requests counter — increments every 400ms
  const reqsDisp = useMemo(() => {
    const n = Math.min(64, Math.floor(elapsed.current / 420));
    return n.toString().padStart(2, "0");
  }, [tick]);

  if (audit?.status === "failed") {
    return (
      <AppShell>
        <Link to="/app/dashboard" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink mb-4 transition-colors">
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="surface-card hard-shadow p-10 max-w-2xl" data-testid="audit-failed-card">
          <div className="label-overline mb-2 text-critical">Status</div>
          <h1 className="font-display text-3xl font-black tracking-tight mb-2 break-all">{audit.url}</h1>
          <div className="border border-critical bg-critical/5 p-4 rounded-sm mt-6" data-testid="audit-failed-message">
            <div className="font-semibold text-critical mb-1">Audit failed</div>
            <div className="text-sm text-ink-muted mono">{audit.error || "Unknown error"}</div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Link to="/app/dashboard" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink mb-4 transition-colors">
        <ArrowLeft size={14} /> Back
      </Link>

      <div
        className="relative overflow-hidden rounded-sm border border-ink text-white"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 20%, rgba(29,78,216,0.18), transparent 60%)," +
            "radial-gradient(ellipse 50% 60% at 100% 100%, rgba(147,51,234,0.12), transparent 60%)," +
            "linear-gradient(135deg, #050507 0%, #0B0B12 50%, #050507 100%)",
          minHeight: "70vh",
        }}
        data-testid="audit-running-stage"
      >
        {/* Network of connecting dots in the background */}
        <ConnectingDots density={0.00009} linkDist={140} speed={0.22} />

        {/* Moving scan beam — top to bottom */}
        <div className="scan-vertical-beam pointer-events-none" />

        {/* Faint scan-line texture for CRT vibe */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-screen"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0px, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 3px)",
          }}
        />

        <div className="relative p-6 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-stretch">
          {/* LEFT — URL, big spinner ring, stage tracker */}
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] font-semibold uppercase mb-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/60">Scanning · live</span>
              <span className="text-white/30 mono">[{audit.id?.slice(0, 8)}]</span>
            </div>

            <h1
              className="font-display font-black tracking-tight leading-[1.05] mb-2 break-all"
              style={{ fontSize: "clamp(1.4rem, 2.4vw, 2.1rem)" }}
              data-testid="audit-running-url"
            >
              {audit.url}
              <span className="caret-blink text-brand">_</span>
            </h1>
            <div className="text-sm text-white/50 mb-8">
              Running a sixty-point technical inspection. This usually
              takes <span className="mono text-white/80">6 – 25 s</span>.
            </div>

            {/* Big spinner ring + counters */}
            <div className="flex items-center gap-8 mb-10">
              <div className="relative">
                <svg width={132} height={132} viewBox="0 0 132 132" className="spin-slow">
                  <circle cx={66} cy={66} r={58} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={4} />
                  <circle
                    cx={66} cy={66} r={58} fill="none"
                    stroke="url(#scan-grad)" strokeWidth={4} strokeLinecap="round"
                    strokeDasharray="60 304"
                  />
                  <defs>
                    <linearGradient id="scan-grad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%"  stopColor="#1D4ED8" />
                      <stop offset="50%" stopColor="#9333EA" />
                      <stop offset="100%" stopColor="#0891B2" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <CircleNotch size={36} weight="bold" className="text-white spin-fast" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-x-8 gap-y-1">
                <Counter label="ELAPSED"   value={`${elapsedDisp}s`} />
                <Counter label="RECEIVED"  value={bytesDisp} />
                <Counter label="REQUESTS"  value={reqsDisp} />
                <Counter label="CHECKS"    value={`${Math.min(60, Math.floor(elapsed.current / 200))}/60`} />
                <Counter label="CATEGORY"  value={STAGES[activeIdx]?.id?.toUpperCase() || "—"} />
                <Counter label="STATUS"    value="WORKING" pulse />
              </div>
            </div>

            {/* Stage tracker */}
            <div className="space-y-2" data-testid="audit-running-stages">
              {STAGES.map((s, i) => {
                const Icon = s.icon;
                const done = i < activeIdx;
                const active = i === activeIdx;
                return (
                  <div
                    key={s.id}
                    className={`flex items-center gap-3 p-2.5 rounded-sm border transition-all duration-300 ${
                      done ? "border-white/10 opacity-50" :
                      active ? "border-white/30 bg-white/[0.04]" :
                      "border-white/5 opacity-30"
                    }`}
                  >
                    <div
                      className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0"
                      style={{
                        background: done ? "rgba(16,185,129,0.18)" :
                                    active ? HUES[i % HUES.length] : "rgba(255,255,255,0.04)",
                      }}
                    >
                      {done
                        ? <CheckCircle size={14} weight="fill" className="text-emerald-400" />
                        : active
                          ? <CircleNotch size={14} weight="bold" className="text-white spin-fast" />
                          : <Icon size={14} weight="bold" className="text-white/40" />}
                    </div>
                    <div className="flex-1 min-w-0 flex items-baseline justify-between gap-3">
                      <div className={`text-sm font-medium ${active ? "text-white" : "text-white/60"}`}>
                        {s.label}
                      </div>
                      <div className="mono text-[10px] text-white/30 truncate">{s.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — live log stream + category badges */}
          <div className="flex flex-col gap-6">
            <div className="bg-black/60 border border-white/10 rounded-sm flex-1 backdrop-blur-sm">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500/70" />
                  <span className="w-2 h-2 rounded-full bg-amber-400/70" />
                  <span className="w-2 h-2 rounded-full bg-emerald-400/70" />
                </div>
                <div className="mono text-[10px] text-white/40 tracking-wider">audit_engine.py — live</div>
                <div className="mono text-[10px] text-white/40">{audit.url?.replace(/^https?:\/\//, "").slice(0, 18)}…</div>
              </div>
              <div className="font-mono text-[11px] leading-relaxed p-4 h-[280px] lg:h-[330px] overflow-hidden flex flex-col justify-end">
                {logs.map((l) => (
                  <div key={l.id} className="log-line whitespace-nowrap" style={{ color: l.hue }}>
                    <span className="text-white/30 mr-2">[{(l.id + 1).toString().padStart(3, "0")}]</span>
                    <span style={{ color: l.hue }}>{l.txt}</span>
                  </div>
                ))}
                <div className="text-emerald-400 mono text-[11px] mt-1">
                  <span className="caret-blink">▍</span>
                </div>
              </div>
            </div>

            {/* Category badges — light up as the stage advances */}
            <div className="grid grid-cols-3 gap-2">
              {[
                ["SEO",  "#1D4ED8", 2],
                ["GEO",  "#0891B2", 3],
                ["AEO",  "#9333EA", 4],
                ["PERF", "#0F766E", 5],
                ["SEC",  "#B91C1C", 6],
                ["A11Y", "#6D28D9", 7],
              ].map(([k, c, threshold]) => {
                const lit = activeIdx >= threshold;
                return (
                  <div
                    key={k}
                    className={`border rounded-sm py-3 text-center transition-all duration-500 ${
                      lit ? "" : "opacity-25"
                    }`}
                    style={{
                      borderColor: lit ? c : "rgba(255,255,255,0.1)",
                      background: lit ? `${c}22` : "transparent",
                    }}
                  >
                    <div className="mono text-[9px] tracking-[0.2em] font-semibold" style={{ color: lit ? c : "rgba(255,255,255,0.4)" }}>
                      {k}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">
                      {lit ? "scanned" : "queued"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spinSlow { to { transform: rotate(360deg); } }
        @keyframes spinFast { to { transform: rotate(360deg); } }
        .spin-slow { animation: spinSlow 6s linear infinite; transform-origin: 50% 50%; }
        .spin-fast { animation: spinFast 0.9s linear infinite; transform-origin: 50% 50%; }
        @keyframes caret { 0%,49% {opacity:1} 50%,100% {opacity:0} }
        .caret-blink { animation: caret 0.9s steps(1) infinite; }
        @keyframes logIn { from { opacity:0; transform: translateY(6px); } to { opacity:1; transform:none; } }
        .log-line { animation: logIn 0.22s ease-out both; }
        @keyframes scanVertical {
          0% { transform: translateY(-10%); }
          100% { transform: translateY(110%); }
        }
        .scan-vertical-beam {
          position: absolute; left: 0; right: 0; height: 90px; pointer-events: none;
          background: linear-gradient(180deg, transparent, rgba(8,145,178,0.18), rgba(147,51,234,0.18), transparent);
          filter: blur(8px);
          animation: scanVertical 4.5s linear infinite;
          mix-blend-mode: screen;
        }
      `}</style>
    </AppShell>
  );
}

function Counter({ label, value, pulse }) {
  return (
    <div>
      <div className="mono text-[9px] tracking-[0.18em] font-semibold text-white/40">{label}</div>
      <div className={`num-display text-xl font-black tabular-nums tracking-tight ${pulse ? "animate-pulse text-emerald-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
